<template>
  <div class="form">
    <Title v-if="step === 'select'">
      {{ title }}
    </Title>

    <Stack
      v-if="errors.length"
      gap="2"
    >
      <Callout
        v-for="(error, index) in errors"
        :key="index"
        status="danger"
      >
        <strong>{{ error.field }}</strong>: {{ error.messages.join(', ') }}
      </Callout>
    </Stack>

    <!-- Step 1: Select (add / move / add-child only) -->
    <template v-if="step === 'select'">
      <StakeAutocomplete
        v-if="mode === 'add' || mode === 'move'"
        :items="potentialParents"
        :has-fetched="!isFetchingOptions"
        :loading="isFetchingOptions"
        :multiple="false"
        key-field="external_id"
        display-field="name"
        value-field="external_id"
        :search-fields="['name', 'email']"
        @selection-change="selectedIds = $event"
      />

      <StakeAutocomplete
        v-else-if="mode === 'add-child'"
        :items="potentialChildren"
        :has-fetched="!isFetchingOptions"
        :loading="isFetchingOptions"
        :multiple="true"
        key-field="external_id"
        display-field="name"
        value-field="external_id"
        :search-fields="['name', 'email']"
        @selection-change="selectedIds = $event"
      />

      <Buttons justify="between">
        <Button
          type="button"
          @click="emit('onClose')"
        >
          Close
        </Button>

        <Button
          variant="primary"
          type="button"
          :disabled="!selectedIds.length"
          icon-after="fas fa-arrow-right"
          @click="step = 'confirm'"
        >
          Continue
        </Button>
      </Buttons>
    </template>

    <!-- Step 2: Confirm -->
    <template v-else>
      <template v-if="mode === 'remove'">
        <Subtitle
          size="5"
          class="confirm-title"
        >
          Are you sure you want to remove <strong>{{ node.entity?.name }}</strong> from {{ parentName }}?
        </Subtitle>
      </template>

      <template v-else-if="mode === 'add'">
        <Subtitle
          size="5"
          class="confirm-title"
        >
          Add <strong>{{ node.entity?.name }}</strong> to <strong>{{ selectedGroup?.name }}</strong>?
        </Subtitle>
      </template>

      <template v-else-if="mode === 'move'">
        <Subtitle
          size="5"
          class="confirm-title"
        >
          Move <strong>{{ node.entity?.name }}</strong> to <strong>{{ selectedGroup?.name }}</strong>?
        </Subtitle>
      </template>

      <Stack
        v-else-if="mode === 'add-child'"
        gap="2"
      >
        <Subtitle
          size="5"
          class="confirm-title"
        >
          Add the following to <strong>{{ node.entity?.name }}</strong>:
        </Subtitle>

        <ul class="confirm-list">
          <li
            v-for="entity in selectedEntities"
            :key="entity.external_id"
          >
            {{ entity.name }}
          </li>
        </ul>
      </Stack>

      <Buttons justify="between">
        <Button
          v-if="mode !== 'remove'"
          type="button"
          @click="step = 'select'"
        >
          Back
        </Button>

        <Button
          v-else
          type="button"
          @click="emit('onClose')"
        >
          Close
        </Button>

        <Button
          variant="primary"
          :loading="isLoading"
          @click="handleSubmit"
        >
          Confirm
        </Button>
      </Buttons>
    </template>
  </div>
</template>

<script>
// Module-level cache — survives component remounts, keyed by entity external_id.
// Cleared after any mutation so stale options are never shown.
const optionsCache = new Map();
</script>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { apiFetch } from '@frontend/api/api';
import { prettifyMixedCase } from '@frontend/shared/Util.js';
import StakeAutocomplete from '@stakeui/StakeAutocomplete.vue';
import { Button, Buttons, Callout, Stack, Subtitle, Title } from '@stakeui';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    // 'add' | 'remove' | 'move' | 'add-child'
  },
  node: {
    type: Object,
    required: true,
  },
  parent: {
    type: Object,
    default: null,
  },
  groupTerm: {
    type: String,
    default: null,
  },
  membersTerm: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['onClose', 'onSuccess']);

// remove goes straight to confirm; all others start at select
const step = ref(props.mode === 'remove' ? 'confirm' : 'select');

const isLoading = ref(false);
const isFetchingOptions = ref(false);
const errors = ref([]);
const selectedIds = ref([]);
const potentialParents = ref([]);
const potentialChildren = ref([]);

const selectedGroup = computed(() =>
  potentialParents.value.find(e => e.external_id === selectedIds.value[0]),
);

const selectedEntities = computed(() =>
  potentialChildren.value.filter(e => selectedIds.value.includes(e.external_id)),
);

const parentName = computed(() => props.parent?.name || props.groupTerm);

const title = computed(() => ({
  'remove': `Remove from ${parentName.value}`,
  'move': `Move to other ${props.groupTerm}`,
  'add-child': `Add ${props.membersTerm} to ${props.node.entity?.name}`,
  'add': `Add ${props.node.entity?.name} to ${props.groupTerm}`,
}[props.mode]));

const normalizeErrors = data =>
  Object.entries(data).map(([field, messages]) => ({
    field: prettifyMixedCase(field),
    messages: Array.isArray(messages) ? messages.map(String) : [String(messages)],
  }));

const fetchRelationshipOptions = async () => {
  const entityId = props.node.entity.external_id;

  if (optionsCache.has(entityId)) {
    const cached = optionsCache.get(entityId);
    potentialParents.value = cached.potential_parents || [];
    potentialChildren.value = cached.potential_children || [];
    return;
  }

  isFetchingOptions.value = true;
  try {
    const res = await apiFetch(`/api/v1/entities/${entityId}/relationship-options/`);
    const data = await res.json();
    optionsCache.set(entityId, data);
    potentialParents.value = data.potential_parents || [];
    potentialChildren.value = data.potential_children || [];
  } catch (error) {
    console.error('Failed to fetch relationship options:', error);
  } finally {
    isFetchingOptions.value = false;
  }
};

onMounted(() => {
  if (props.mode !== 'remove') {
    fetchRelationshipOptions();
  }
});

const handleSubmit = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  errors.value = [];

  try {
    if (props.mode === 'remove') {
      const res = await apiFetch(`/api/v1/entities/${props.node.entity.external_id}/relationships/remove/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationship_type: 'parent', related_entity_id: props.parent?.external_id }),
      });
      if (!res.ok) {
        errors.value = normalizeErrors(await res.json());
        return;
      }
    } else if (props.mode === 'add' || props.mode === 'move') {
      const res = await apiFetch(`/api/v1/entities/${props.node.entity.external_id}/relationships/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationship_type: 'parent', related_entity_id: selectedIds.value[0] }),
      });
      if (!res.ok) {
        errors.value = normalizeErrors(await res.json());
        return;
      }
    } else if (props.mode === 'add-child') {
      const results = await Promise.all(
          selectedIds.value.map(childId =>
            apiFetch(`/api/v1/entities/${childId}/relationships/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ relationship_type: 'parent', related_entity_id: props.node.entity.external_id }),
            }),
          ),
      );
      const failed = results.filter(r => !r.ok);
      if (failed.length) {
        errors.value = [{ field: 'error', messages: [`${failed.length} member(s) could not be added. Please try again.`] }];
        return;
      }
    }

    optionsCache.clear();
    const messages = {
      'remove': `Successfully removed from ${parentName.value}`,
      'add': `Successfully added to ${props.groupTerm}`,
      'move': `Successfully moved to new ${props.groupTerm}`,
      'add-child': `Successfully added ${selectedIds.value.length} ${props.membersTerm} to ${props.groupTerm}`,
    };
    Toast.open({ message: messages[props.mode], type: 'is-success', duration: 5000 });
    emit('onSuccess');
  } catch (error) {
    console.error('Error updating membership:', error);
    errors.value = [{ field: 'error', messages: ['Failed to update. Please try again.'] }];
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.form {
  width: 30em;
  padding: 0.75em 1em;
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.confirm-title {
  line-height: 1.25;
}

.confirm-list {
  margin: 0;
  font-size: 1.125em;
  padding-left: 1.25em;
  list-style: disc;
}
</style>
